/**
 * View 定義サービス
 * mock API から取得した View 定義をキャッシュし、表示・編集・一括入力・フィルターの
 * 候補定義や既定値、表示ラベル、データ型を一元的に提供する
 */
import { Injectable } from '@angular/core';
import { DEFAULT_VIEW_CD } from '../../../core/constants/app.defaults';
import { environment } from '../../../../environments/environment';
import type {
  FieldEditorConfig,
  FieldEditorType,
  FieldOptionSourceDefinition,
} from '../models/field-editor.model';

type FieldDataType = 'string' | 'number' | 'date';
type ViewDefinitionSectionKey = 'display' | 'edit' | 'bulk_input' | 'filter';
type ViewDisplayUnit = 'sku' | 'item';
export type ViewFooterActionTabType = 'all' | 'custom';
export type ViewFooterButtonActionId = 'pdf' | 'generation-url';
export type ViewFooterDataActionId = 'lot-add' | 'lot-delete' | 'item-add' | 'item-exclude';

export interface ViewFooterActionDefinition<TActionId extends string = string> {
  id: TActionId;
  name?: string;
}

export interface ViewFooterActionsScopeDefinition {
  button_actions?: ViewFooterActionDefinition<ViewFooterButtonActionId>[];
  data_actions_by_tab_type?: Partial<Record<ViewFooterActionTabType, ViewFooterActionDefinition<ViewFooterDataActionId>[]>>;
}

interface ViewInfoDefinition {
  cd: number;
  name: string;
  description?: string;
  config_scope: string;
  all_tab?: boolean;
}

interface BaseFieldDefinition {
  label: string;
  data_type: FieldDataType;
  option_source?: FieldOptionSourceDefinition;
}

interface DisplayCandidateDefinition {
  field_key: string;
  label?: string;
  default_visible: boolean;
  default_order: number | null;
}

interface EditCandidateDefinition {
  field_key: string;
  label?: string;
  default_editable: boolean;
  editor_type?: FieldEditorType;
  option_source?: FieldOptionSourceDefinition;
}

interface BulkInputCandidateDefinition {
  field_key: string;
  label?: string;
  default_enabled: boolean;
}

interface FilterCandidateDefinition {
  field_key: string;
  label?: string;
  default_enabled: boolean;
}

interface ViewScopeSettingDefinition {
  footer_actions?: ViewFooterActionsScopeDefinition;
}

export interface ViewDefinitionResponse {
  schema_version: string;
  view: ViewInfoDefinition;
  field_definitions: Record<string, BaseFieldDefinition>;
  display: {
    description?: string;
    candidates: DisplayCandidateDefinition[];
    item_candidates?: DisplayCandidateDefinition[];
  };
  edit: {
    description?: string;
    candidates: EditCandidateDefinition[];
  };
  bulk_input: {
    description?: string;
    candidates: BulkInputCandidateDefinition[];
  };
  filter: {
    description?: string;
    candidates: FilterCandidateDefinition[];
  };
  scope_setting?: ViewScopeSettingDefinition;
}

type ViewSectionCandidate = DisplayCandidateDefinition | EditCandidateDefinition | BulkInputCandidateDefinition | FilterCandidateDefinition;

@Injectable({ providedIn: 'root' })
export class ViewDefinitionService {
  private static readonly localDefinitionQueryParamName = 'definitionSource';
  private static readonly localDefinitionQueryParamValue = 'local';
  private static readonly supportedFooterButtonActionIds = new Set<ViewFooterButtonActionId>([
    'pdf',
    'generation-url',
  ]);
  private static readonly supportedFooterDataActionIds = new Set<ViewFooterDataActionId>([
    'lot-add',
    'lot-delete',
    'item-add',
    'item-exclude',
  ]);

  /** 後端から取得する View 定義の frontendCd 一覧 */
  private static readonly frontendDefinitionCodes: Readonly<Record<number, string>> = {
    1: 'group-list-hub-manager-view-cd-1-definition',
    2: 'group-list-hub-manager-view-cd-2-definition',
    3: 'group-list-hub-manager-view-cd-3-definition',
    4: 'group-list-hub-manager-view-cd-4-definition',
  };

  /** 本地 mock 時代に利用していた View 定義ファイル一覧。比較用に保持する */
  private static readonly mockDefinitionPaths: Readonly<Record<number, string>> = {
    1: '/mock-api/view-cd-1-initial-definition.json',
    2: '/mock-api/view-cd-2-initial-definition.json',
    3: '/mock-api/view-cd-3-initial-definition.json',
    4: '/mock-api/view-cd-4-initial-definition.json',
  };

  /** View CD ごとに読み込んだ定義キャッシュ */
  private readonly definitions = new Map<number, ViewDefinitionResponse>();

  /** mock API から定義を取得してキャッシュする */
  async preloadDefinitions(): Promise<void> {
    await Promise.all(
      Object.entries(ViewDefinitionService.mockDefinitionPaths).map(async ([rawViewCd, path]) => {
        const viewCd = Number(rawViewCd);
        const definition = await this.fetchDefinition(path);
        if (definition) {
          this.definitions.set(viewCd, definition);
        }
      }),
    );
  }

  /** 起動時に読み込む backend 定義の対象一覧を返す */
  getFrontendDefinitionTargets(): Array<{ viewCd: number; frontendCode: string }> {
    return Object.entries(ViewDefinitionService.frontendDefinitionCodes).map(([rawViewCd, frontendCode]) => ({
      viewCd: Number(rawViewCd),
      frontendCode,
    }));
  }

  /** 取得済みの定義一式でキャッシュを置き換える */
  replaceDefinitions(definitions: ReadonlyMap<number, ViewDefinitionResponse>): void {
    this.definitions.clear();

    for (const [viewCd, definition] of definitions.entries()) {
      this.definitions.set(viewCd, definition);
    }
  }

  /** 開発環境のみ、URL 指定で本地 JSON を優先して読み込む。 */
  shouldUseLocalDefinitions(): boolean {
    if (environment.production || typeof window === 'undefined') {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    return params.get(ViewDefinitionService.localDefinitionQueryParamName) === ViewDefinitionService.localDefinitionQueryParamValue;
  }

  /** 本地 JSON から 4 つの View 定義をまとめて読み込む。 */
  async loadLocalDefinitions(): Promise<ReadonlyMap<number, ViewDefinitionResponse>> {
    const loadedDefinitions = await Promise.all(
      Object.entries(ViewDefinitionService.mockDefinitionPaths).map(async ([rawViewCd, path]) => ({
        viewCd: Number(rawViewCd),
        definition: await this.fetchDefinition(path),
      })),
    );

    return new Map(
      loadedDefinitions
        .filter((entry): entry is { viewCd: number; definition: ViewDefinitionResponse } => entry.definition !== null)
        .map(entry => [entry.viewCd, entry.definition]),
    );
  }

  /** 開発者向けの本地定義切り替え方法を返す。 */
  getLocalDefinitionUsageHint(): string {
    return `append ?${ViewDefinitionService.localDefinitionQueryParamName}=${ViewDefinitionService.localDefinitionQueryParamValue} to the URL in development to load public/mock-api view definitions.`;
  }

  getViewName(viewCd: number = DEFAULT_VIEW_CD): string | null {
    return this.definitions.get(viewCd)?.view.name ?? null;
  }

  /** Item 表示用の候補定義が存在するかどうかを返す */
  hasItemDisplayCandidates(viewCd: number = DEFAULT_VIEW_CD): boolean {
    const itemCandidates = this.definitions.get(viewCd)?.display.item_candidates;
    return Array.isArray(itemCandidates) && itemCandidates.length > 0;
  }

  /** ALL Tab を表示するかどうかを返す */
  shouldShowAllTab(viewCd: number = DEFAULT_VIEW_CD): boolean {
    return this.definitions.get(viewCd)?.view.all_tab !== false;
  }

  /** scope_setting からボタン系 footer action 定義を取得する */
  getFooterButtonActionDefinitions(viewCd: number = DEFAULT_VIEW_CD): ViewFooterActionDefinition<ViewFooterButtonActionId>[] {
    const definitions = this.definitions.get(viewCd)?.scope_setting?.footer_actions?.button_actions ?? [];
    return this.normalizeFooterActionDefinitions(definitions, ViewDefinitionService.supportedFooterButtonActionIds);
  }

  /** scope_setting からタブ種別別のデータ操作 action 定義を取得する */
  getFooterDataActionDefinitions(
    viewCd: number = DEFAULT_VIEW_CD,
    tabType: ViewFooterActionTabType | null,
  ): ViewFooterActionDefinition<ViewFooterDataActionId>[] {
    if (!tabType) {
      return [];
    }

    const definitions = this.definitions.get(viewCd)?.scope_setting?.footer_actions?.data_actions_by_tab_type?.[tabType] ?? [];
    return this.normalizeFooterActionDefinitions(definitions, ViewDefinitionService.supportedFooterDataActionIds);
  }

  /**
   * フィールド表示名を取得する
   * section 側に label 上書きがある場合はそちらを優先する
   */
  getFieldLabel(
    viewCd: number,
    fieldKey: string,
    section?: ViewDefinitionSectionKey,
    displayUnit: ViewDisplayUnit = 'sku',
  ): string {
    const sectionLabel = this.getSectionCandidate(viewCd, section, fieldKey, displayUnit)?.label;
    if (sectionLabel && sectionLabel.trim() !== '') {
      return sectionLabel;
    }

    const fieldLabel = this.definitions.get(viewCd)?.field_definitions[fieldKey]?.label;
    if (fieldLabel && fieldLabel.trim() !== '') {
      return fieldLabel;
    }

    return fieldKey;
  }

  getFieldDataType(viewCd: number, fieldKey: string): FieldDataType {
    return this.definitions.get(viewCd)?.field_definitions[fieldKey]?.data_type ?? 'string';
  }

  /**
   * フィールドの編集設定を取得する
   * editor_type が未定義なら data_type から自然な既定値へフォールバックする
   */
  getFieldEditorConfig(viewCd: number, fieldKey: string): FieldEditorConfig {
    const fieldDefinition = this.definitions.get(viewCd)?.field_definitions[fieldKey];
    const editCandidate = this.definitions.get(viewCd)?.edit.candidates
      .find(candidate => candidate.field_key === fieldKey);
    const optionSource = editCandidate?.option_source ?? fieldDefinition?.option_source ?? null;

    return {
      editorType: editCandidate?.editor_type
        ?? (optionSource ? 'select' : this.resolveDefaultEditorType(this.getFieldDataType(viewCd, fieldKey))),
      optionSource,
    };
  }

  getDisplayCandidateKeys(viewCd: number = DEFAULT_VIEW_CD, displayUnit: ViewDisplayUnit = 'sku'): string[] {
    const candidates = this.getDisplayCandidates(viewCd, displayUnit);
    if (!candidates) {
      return this.getDefinedFieldKeys(viewCd);
    }

    return candidates.map(candidate => candidate.field_key);
  }

  /** 表示定義から既定の表示列一覧を取得する */
  getDefaultVisibleKeys(viewCd: number = DEFAULT_VIEW_CD, displayUnit: ViewDisplayUnit = 'sku'): string[] {
    const candidates = this.getDisplayCandidates(viewCd, displayUnit);
    if (!candidates) {
      return [];
    }

    return candidates
      .filter(candidate => candidate.default_visible)
      .sort((left, right) => this.compareDisplayCandidates(left, right, candidates))
      .map(candidate => candidate.field_key);
  }

  /** 表示設定全体の既定並び順を構築する */
  getDefaultDisplayFieldOrder(viewCd: number = DEFAULT_VIEW_CD, displayUnit: ViewDisplayUnit = 'sku'): string[] {
    const candidates = this.getDisplayCandidates(viewCd, displayUnit);
    if (!candidates) {
      return this.getDefinedFieldKeys(viewCd);
    }

    const visibleKeys = this.getDefaultVisibleKeys(viewCd, displayUnit);
    const remainingKeys = candidates
      .map(candidate => candidate.field_key)
      .filter(fieldKey => !visibleKeys.includes(fieldKey));

    return [...visibleKeys, ...remainingKeys];
  }

  /** 編集候補列の一覧を取得する */
  getEditableCandidateKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.edit.candidates;
    if (!candidates) {
      return [];
    }

    return candidates.map(candidate => candidate.field_key);
  }

  /** 既定で編集可能な列一覧を取得する */
  getDefaultEditableKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.edit.candidates;
    if (!candidates) {
      return [];
    }

    return candidates
      .filter(candidate => candidate.default_editable)
      .map(candidate => candidate.field_key);
  }

  /** 一括入力候補列の一覧を取得する */
  getBulkInputCandidateKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.bulk_input.candidates;
    if (!candidates) {
      return [];
    }

    return candidates.map(candidate => candidate.field_key);
  }

  /** 既定で一括入力に表示する列一覧を取得する */
  getDefaultBulkInputKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.bulk_input.candidates;
    if (!candidates) {
      return [];
    }

    return candidates
      .filter(candidate => candidate.default_enabled)
      .map(candidate => candidate.field_key);
  }

  /** フィルター候補列の一覧を取得する */
  getFilterCandidateKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.filter.candidates;
    if (!candidates) {
      return [];
    }

    return candidates.map(candidate => candidate.field_key);
  }

  /** 既定で有効なフィルター列一覧を取得する */
  getDefaultEnabledFilterKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
    const candidates = this.definitions.get(viewCd)?.filter.candidates;
    if (!candidates) {
      return [];
    }

    return candidates
      .filter(candidate => candidate.default_enabled)
      .map(candidate => candidate.field_key);
  }

  /** 指定セクションの候補定義を取得する */
  private getSectionCandidate(
    viewCd: number,
    section: ViewDefinitionSectionKey | undefined,
    fieldKey: string,
    displayUnit: ViewDisplayUnit = 'sku',
  ): ViewSectionCandidate | null {
    if (!section) {
      return null;
    }

    const definition = this.definitions.get(viewCd);
    if (!definition) {
      return null;
    }

    if (section === 'display') {
      return this.getDisplayCandidates(viewCd, displayUnit)?.find(candidate => candidate.field_key === fieldKey) ?? null;
    }

    return definition[section].candidates.find(candidate => candidate.field_key === fieldKey) ?? null;
  }

  /** 表示単位に応じた表示候補定義を取得する */
  private getDisplayCandidates(
    viewCd: number,
    displayUnit: ViewDisplayUnit,
  ): DisplayCandidateDefinition[] | undefined {
    const displayDefinition = this.definitions.get(viewCd)?.display;
    if (!displayDefinition) {
      return undefined;
    }

    if (displayUnit === 'item' && displayDefinition.item_candidates && displayDefinition.item_candidates.length > 0) {
      return displayDefinition.item_candidates;
    }

    return displayDefinition.candidates;
  }

  private getDefinedFieldKeys(viewCd: number): string[] {
    return Object.keys(this.definitions.get(viewCd)?.field_definitions ?? {});
  }

  /** default_order を考慮して表示候補の順序を比較する */
  private compareDisplayCandidates(
    left: DisplayCandidateDefinition,
    right: DisplayCandidateDefinition,
    allCandidates: DisplayCandidateDefinition[],
  ): number {
    if (left.default_order !== null && right.default_order !== null) {
      return left.default_order - right.default_order;
    }

    if (left.default_order !== null) {
      return -1;
    }

    if (right.default_order !== null) {
      return 1;
    }

    return allCandidates.indexOf(left) - allCandidates.indexOf(right);
  }

  /** scope_setting の action 定義をサポート対象に絞り込みつつ順序保持で正規化する */
  private normalizeFooterActionDefinitions<TActionId extends string>(
    definitions: ViewFooterActionDefinition<TActionId>[],
    supportedActionIds: ReadonlySet<TActionId>,
  ): ViewFooterActionDefinition<TActionId>[] {
    const seenIds = new Set<TActionId>();

    return definitions.filter(definition => {
      if (!supportedActionIds.has(definition.id) || seenIds.has(definition.id)) {
        return false;
      }

      seenIds.add(definition.id);
      return true;
    });
  }

  /** data_type だけで十分な既定エディタを解決する */
  private resolveDefaultEditorType(dataType: FieldDataType): FieldEditorType {
    switch (dataType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  }

  /** キー配列を候補集合に沿って正規化する */
  private normalizeOrderedKeys(keys: string[], fallbackOrder: string[]): string[] {
    const allowedKeys = new Set(fallbackOrder);
    const normalized: string[] = [];

    for (const key of keys) {
      if (allowedKeys.has(key) && !normalized.includes(key)) {
        normalized.push(key);
      }
    }

    for (const key of fallbackOrder) {
      if (!normalized.includes(key)) {
        normalized.push(key);
      }
    }

    return normalized;
  }

  /** mock API から View 定義レスポンスを取得する */
  private async fetchDefinition(path: string): Promise<ViewDefinitionResponse | null> {
    try {
      const response = await fetch(path, { method: 'GET' });
      if (!response.ok) {
        return null;
      }

      return await response.json() as ViewDefinitionResponse;
    } catch {
      return null;
    }
  }
}
